/**
 * Centralized API client with JWT authentication
 * Automatically handles token management and refresh
 */
import {
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
  clearTokens,
  hasValidSession,
} from '../services/authService';
import { API_BASE_URL } from './apiBaseUrl';

/**
 * FastAPI raises HTTPException(detail=...) where `detail` may be a string,
 * an object (e.g. credit middleware returns
 * `{ "error": "...", "message": "...", "action": "..." }`), or a list of
 * Pydantic validation errors. Coerce any of those shapes into a readable
 * string so callers don't surface "[object Object]" to the user.
 */
const extractErrorMessage = (errorData: any, fallback: string): string => {
  const detail = errorData?.detail;

  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }

  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.error === 'string') return detail.error;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object' && typeof first.msg === 'string') {
        const loc = Array.isArray(first.loc) ? first.loc.join('.') : '';
        return loc ? `${loc}: ${first.msg}` : first.msg;
      }
    }
    try {
      return JSON.stringify(detail);
    } catch {
      // fall through to other fields
    }
  }

  if (typeof errorData?.message === 'string' && errorData.message.trim().length > 0) {
    return errorData.message;
  }

  return fallback;
};

/**
 * Returns the structured `{ error, message, action, ... }` object that the
 * credit middleware (and similar gates) puts on `detail`, or `null` for the
 * unstructured/string/list shapes.
 */
const getStructuredDetail = (errorData: any): Record<string, any> | null => {
  const detail = errorData?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as Record<string, any>;
  }
  return null;
};

/**
 * Friendly user-facing copy for the structured "restricted action" errors
 * raised by the credit middleware (`action_not_configured`, `action_disabled`).
 * The raw backend message is intentionally not surfaced to the user — it is
 * a developer-oriented description (e.g. "Action 'ai_reply_generate' is not
 * configured in action_pricing").
 */
const FRIENDLY_RESTRICTED_MESSAGES: Record<string, { title: string; message: string }> = {
  action_not_configured: {
    title: 'Feature unavailable',
    message:
      "This feature isn't available right now. Our team has been notified — please try again later or contact support.",
  },
  action_disabled: {
    title: 'Feature disabled',
    message:
      'This feature is temporarily disabled on your account. Please contact support if you need it enabled.',
  },
};

/**
 * Centralized handler for non-2xx responses. Recognises the structured errors
 * raised by the credit middleware so the whole app can surface consistent UI
 * (modal for insufficient credits, toast for restricted actions) without
 * every page having to reimplement it.
 *
 * Always throws — the return type is `never`.
 */
const throwForResponse = async (response: Response): Promise<never> => {
  const errorData = await response.json().catch(() => ({} as any));
  const detail = getStructuredDetail(errorData);
  const errorCode = typeof detail?.error === 'string' ? detail.error : undefined;

  // 402 Payment Required — insufficient credits. Keep the existing modal flow.
  if (response.status === 402) {
    const detailObj = detail || {};
    const creditError = new Error(
      (typeof detailObj.message === 'string' && detailObj.message) ||
        'Insufficient credits for this action.'
    );
    (creditError as any).status = 402;
    (creditError as any).code = 'insufficient_credits';
    (creditError as any).creditInfo = {
      required: detailObj.required,
      balance: detailObj.balance,
      action: detailObj.action,
    };
    document.dispatchEvent(
      new CustomEvent('insufficient-credits', { detail: detailObj })
    );
    throw creditError;
  }

  // Restricted action errors (action_not_configured / action_disabled).
  // These come back as 500 / 403 respectively but share the same structured
  // shape. We surface them via a global toast and a friendly Error so any
  // inline error UI also reads well.
  if (errorCode && FRIENDLY_RESTRICTED_MESSAGES[errorCode]) {
    const friendly = FRIENDLY_RESTRICTED_MESSAGES[errorCode];
    const action = typeof detail?.action === 'string' ? detail.action : undefined;
    document.dispatchEvent(
      new CustomEvent('action-restricted', {
        detail: {
          kind: errorCode,
          action,
          title: friendly.title,
          message: friendly.message,
          rawMessage: typeof detail?.message === 'string' ? detail.message : undefined,
          status: response.status,
        },
      })
    );
    const restrictedError = new Error(friendly.message);
    (restrictedError as any).status = response.status;
    (restrictedError as any).code = errorCode;
    (restrictedError as any).action = action;
    throw restrictedError;
  }

  // 403 Forbidden (non-credit) — generic permissions error.
  if (response.status === 403) {
    throw new Error(
      extractErrorMessage(errorData, 'Access denied. Insufficient permissions.')
    );
  }

  // Generic fallback.
  throw new Error(
    extractErrorMessage(errorData, `API request failed: ${response.statusText}`)
  );
};

// Default timeout for API requests (ms). Long-running endpoints can override
// via the `timeoutMs` option (use 0 to disable the timeout entirely).
const DEFAULT_TIMEOUT_MS = 90_000;

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
  /** Override timeout in ms. 0 disables the timeout. Defaults to 90s. */
  timeoutMs?: number;
}

/**
 * Wraps fetch with an AbortController-driven timeout so requests cannot hang
 * forever. Throws a friendly Error when the timeout fires.
 */
const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> => {
  if (!timeoutMs || timeoutMs <= 0) {
    return fetch(input, init);
  }

  const controller = new AbortController();
  // Compose with any caller-provided signal
  const callerSignal = init.signal;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort((callerSignal as any).reason);
    } else {
      callerSignal.addEventListener('abort', () => controller.abort((callerSignal as any).reason), { once: true });
    }
  }

  const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (err: any) {
    if (err?.name === 'AbortError' || err?.name === 'TimeoutError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s — please retry.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Makes an authenticated API request using JWT
 * Automatically handles token refresh
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { skipAuth = false, timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  
  const headers = new Headers(fetchOptions.headers);
  headers.set('Content-Type', 'application/json');
  
  // Add authorization header if not skipping auth
  if (!skipAuth) {
    // Check if token needs refresh before making request
    if (hasValidSession() && isTokenExpired()) {
      console.log('[API Client] Token expired, refreshing...');
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        console.warn('[API Client] Token refresh failed, redirecting to login');
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }
    }
    
    const accessToken = getAccessToken();
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    } else {
      // No token available - redirect to login
      console.warn('[API Client] No access token available, redirecting to login');
      window.location.href = '/login';
      throw new Error('Not authenticated. Please log in.');
    }
  }
  
  // Make the API request (with timeout)
  const response = await fetchWithTimeout(
    `${API_BASE_URL}${endpoint}`,
    { ...fetchOptions, headers },
    timeoutMs
  );
  
  // Handle 401 Unauthorized - try to refresh token only if we had a session
  if (response.status === 401 && !skipAuth && hasValidSession()) {
    console.warn('[API Client] 401 Unauthorized - attempting token refresh');
    
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry with new token
      const newToken = getAccessToken();
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        const retryResponse = await fetchWithTimeout(
          `${API_BASE_URL}${endpoint}`,
          { ...fetchOptions, headers },
          timeoutMs
        );
        
        if (retryResponse.ok) {
          try {
            return await retryResponse.json();
          } catch (e) {
            return {} as T;
          }
        }

        // Still failing after refresh — fall through to the shared handler so
        // restricted-action / credit errors are surfaced consistently.
        await throwForResponse(retryResponse);
      }
    }
    
    // Refresh failed, clear tokens and redirect
    console.log('[API Client] Token refresh failed, redirecting to login');
    clearTokens();
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  // Any non-2xx — delegate to the shared handler (handles 402 credit modal,
  // restricted-action toasts, and generic errors).
  if (!response.ok) {
    await throwForResponse(response);
  }

  // Parse and return JSON response
  try {
    return await response.json();
  } catch (e) {
    // If response is not JSON, return empty object
    return {} as T;
  }
};

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),
  
  post: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
  
  put: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
  
  patch: <T = any>(endpoint: string, data?: any, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
  
  delete: <T = any>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

// Legacy export for backward compatibility
export const setGlobalLogoutHandler = (handler: () => void) => {
  // No-op - handled by auth service now
};
